import { FC, useState } from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { Box, Button } from "@mui/material";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";

interface StyledInputProps {
  placeholder?: string;
  handleSearchClick?: (searchTerm: string) => void;
  handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  fullWidth?: boolean;
  value?: string;
  clearSearch?: () => void;
  hideBtn?: boolean;
}

const StyledInput: FC<StyledInputProps> = ({
  placeholder = "Search...",
  handleSearchClick,
  handleChange,
  value,
  fullWidth = false,
  clearSearch,
  hideBtn,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<any>(value || "");
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const internalHandleChange: React.ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const value = event.target.value;
    setSearchTerm(value);
    handleChange?.(event);
  };

  const handleIconClick = () => handleSearchClick?.(searchTerm);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearchClick?.(searchTerm);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: fullWidth ? "100%" : "auto",
        flexWrap: { xs: "wrap", sm: "nowrap" },
      }}
    >
      <OutlinedInput
        className={`font-Saira italic font-[500] text-[13px] ${fullWidth ? "w-[100%]" : "w-[340px]"}
          overflow-hidden pr-0 h-[38px] rounded-[8px] bg-white`}
        sx={{
          flex: fullWidth ? "1 1 auto" : undefined,
          minWidth: 0,
          height: 38,
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#343434 !important",
            borderWidth: "2px !important",
          },
          "& input:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 30px white inset !important",
          },
          "& input": { paddingLeft: "0 !important" },
        }}
        id="styled-input"
        type="text"
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon
              className="size-5"
              sx={{ color: themeColors?.primary }}
            />
          </InputAdornment>
        }
        placeholder={t(placeholder)}
        onChange={internalHandleChange}
        onKeyDown={handleKeyPress}
        value={searchTerm}
        inputProps={{
          title: t(placeholder),
        }}
      />
      {hideBtn ? null : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
            height: 38,
          }}
        >
          <Button
            variant="contained"
            disabled={false}
            onClick={handleIconClick}
            sx={{
              boxShadow: "none",
              background: themeColors?.primary,
              height: 38,
              minHeight: 38,
              py: 0,
              px: 3,
              lineHeight: 1.2,
            }}
            className={`text-white text-ellipsis line-clamp-1 overflow-hidden font-Saira font-[600] text-[13px]`}
          >
            {t("submit")}
          </Button>
          {value ? (
            <Button
              variant="contained"
              onClick={() => {
                clearSearch?.();
                setSearchTerm("");
              }}
              sx={{
                boxShadow: "none",
                color: themeColors?.primary,
                background: "#d8d1e0",
                height: 38,
                minHeight: 38,
                py: 0,
                px: 3,
                lineHeight: 1.2,
              }}
              className={`text-ellipsis overflow-hidden font-Saira font-[600] text-[13px]`}
            >
              {t("clear")}
            </Button>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default StyledInput;
